import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link, Routes, Route, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Building2, Users, DollarSign, BarChart3, 
  Menu, X, LogOut, ChevronDown, Plus, Pencil, Trash2, 
  Calendar, TrendingUp, IndianRupee, Loader2, Settings,
  ArrowUpRight, ArrowDownRight, Tag, ToggleLeft, ToggleRight, Percent, ShieldCheck
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============== Helper Functions ==============
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

// ============== Super Admin Sidebar ==============
const SuperAdminSidebar = ({ activeTab, setActiveTab, isOpen, setIsOpen }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'reports', label: 'Reports', icon: BarChart3 },
    { id: 'facilities', label: 'Facilities', icon: Building2 },
    { id: 'coupons', label: 'Coupons', icon: Tag },
    { id: 'commission', label: 'Aggregator Commission', icon: Percent },
    { id: 'users', label: 'Admin Users', icon: Users },
    { id: 'access_audit', label: 'Access Audit', icon: ShieldCheck },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full bg-slate-900 text-white z-50
        w-64 transform transition-transform duration-300
        lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-lg font-bold text-emerald-400">TRP Sports</h1>
                <p className="text-xs text-slate-400">Super Admin</p>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="lg:hidden text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    setIsOpen(false);
                  }}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 rounded-lg
                    transition-colors text-left
                    ${activeTab === item.id 
                      ? 'bg-emerald-600 text-white' 
                      : 'text-slate-300 hover:bg-slate-800'
                    }
                  `}
                  data-testid={`nav-${item.id}`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* User Info & Logout */}
          <div className="p-4 border-t border-slate-700">
            <div className="mb-3">
              <p className="text-sm font-medium text-white">{user?.name}</p>
              <p className="text-xs text-slate-400">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-4 py-2 text-red-400 hover:bg-red-900/30 rounded-lg transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// ============== Dashboard Overview ==============
const DashboardOverview = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [drilldownView, setDrilldownView] = useState(null); // null | { type: 'dues'|'events', facilityId, facilityName }
  const [drilldownData, setDrilldownData] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  // Date state
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState(monthStart.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);
  const [datePreset, setDatePreset] = useState('this_month');

  // Drilldown date overrides
  const [ddStartDate, setDdStartDate] = useState('');
  const [ddEndDate, setDdEndDate] = useState('');

  const applyPreset = (preset) => {
    setDatePreset(preset);
    const now = new Date();
    let s, e;
    switch(preset) {
      case 'this_month':
        s = new Date(now.getFullYear(), now.getMonth(), 1);
        e = now;
        break;
      case 'last_month':
        s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        e = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'this_quarter':
        const qm = Math.floor(now.getMonth() / 3) * 3;
        s = new Date(now.getFullYear(), qm, 1);
        e = now;
        break;
      case 'this_year':
        s = new Date(now.getFullYear(), 0, 1);
        e = now;
        break;
      default: return;
    }
    setStartDate(s.toISOString().split('T')[0]);
    setEndDate(e.toISOString().split('T')[0]);
  };

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/superadmin/dashboard?start_date=${startDate}&end_date=${endDate}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load dashboard');
      const data = await response.json();
      setDashboardData(data);
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDashboard(); }, [startDate, endDate]);

  const openDrilldown = async (type, facilityId, facilityName) => {
    setDrilldownView({ type, facilityId, facilityName });
    setDdStartDate(startDate);
    setDdEndDate(endDate);
    await loadDrilldownData(type, facilityId, startDate, endDate);
  };

  const loadDrilldownData = async (type, facilityId, sd, ed) => {
    setDrilldownLoading(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = type === 'dues' ? 'pending-dues' : 'events';
      const res = await fetch(`${API_URL}/api/superadmin/facility/${facilityId}/${endpoint}?start_date=${sd}&end_date=${ed}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load data');
      setDrilldownData(await res.json());
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setDrilldownLoading(false);
    }
  };

  const handleDdDateChange = () => {
    if (drilldownView) {
      loadDrilldownData(drilldownView.type, drilldownView.facilityId, ddStartDate, ddEndDate);
    }
  };

  // Drilldown: Pending Dues page
  if (drilldownView?.type === 'dues') {
    return (
      <div className="space-y-4" data-testid="pending-dues-drilldown">
        <div className="flex items-center gap-3">
          <button onClick={() => { setDrilldownView(null); setDrilldownData(null); }} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold">Pending Dues</h1>
            <p className="text-sm text-slate-500">{drilldownView.facilityName}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="text-xs text-gray-500">From</label>
            <input type="date" value={ddStartDate} onChange={e => setDdStartDate(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm ml-1" />
          </div>
          <div>
            <label className="text-xs text-gray-500">To</label>
            <input type="date" value={ddEndDate} onChange={e => setDdEndDate(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm ml-1" />
          </div>
          <Button size="sm" onClick={handleDdDateChange}>Apply</Button>
        </div>
        {drilldownLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : drilldownData ? (
          <>
            <div className="flex gap-4 text-sm">
              <span className="font-medium">{drilldownData.count} bookings</span>
              <span className="text-orange-600 font-bold">Total Pending: {formatCurrency(drilldownData.total_pending)}</span>
            </div>
            <div className="space-y-2">
              {drilldownData.dues?.map((d, i) => (
                <div key={i} className="bg-white border rounded-xl p-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{d.customer_name}</p>
                    <p className="text-xs text-gray-500">{d.date} | {d.booking_type.toUpperCase()} | {d.customer_phone}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-orange-600">{formatCurrency(d.pending_amount)}</p>
                    <p className="text-[10px] text-gray-400">of {formatCurrency(d.total_amount)}</p>
                  </div>
                </div>
              ))}
              {drilldownData.dues?.length === 0 && <p className="text-sm text-gray-400 text-center py-6">No pending dues</p>}
            </div>
          </>
        ) : null}
      </div>
    );
  }

  // Drilldown: Events page
  if (drilldownView?.type === 'events') {
    return (
      <div className="space-y-4" data-testid="events-drilldown">
        <div className="flex items-center gap-3">
          <button onClick={() => { setDrilldownView(null); setDrilldownData(null); }} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold">Events</h1>
            <p className="text-sm text-slate-500">{drilldownView.facilityName}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="text-xs text-gray-500">From</label>
            <input type="date" value={ddStartDate} onChange={e => setDdStartDate(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm ml-1" />
          </div>
          <div>
            <label className="text-xs text-gray-500">To</label>
            <input type="date" value={ddEndDate} onChange={e => setDdEndDate(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm ml-1" />
          </div>
          <Button size="sm" onClick={handleDdDateChange}>Apply</Button>
        </div>
        {drilldownLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : drilldownData ? (
          <>
            <div className="flex gap-4 text-sm">
              <span className="font-medium">{drilldownData.total_events} events</span>
              <span className="text-emerald-600 font-bold">Revenue: {formatCurrency(drilldownData.total_revenue)}</span>
              <span className="text-blue-600">{drilldownData.total_hours}h booked</span>
            </div>
            <div className="space-y-2">
              {drilldownData.events?.map((ev, i) => (
                <div key={i} className="bg-white border rounded-xl p-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{ev.customer_name}</p>
                    <p className="text-xs text-gray-500">{ev.date} | {ev.start_time}-{ev.end_time} | {ev.hours_booked}h</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold">{formatCurrency(ev.amount)}</p>
                    {ev.court_name && <p className="text-[10px] text-gray-400">{ev.court_name}</p>}
                  </div>
                </div>
              ))}
              {drilldownData.events?.length === 0 && <p className="text-sm text-gray-400 text-center py-6">No events in this period</p>}
            </div>
          </>
        ) : null}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!dashboardData) return null;

  const { overall, by_facility, period } = dashboardData;

  return (
    <div className="space-y-6" data-testid="superadmin-dashboard">
      {/* Header + Date Selector */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {[
            { id: 'this_month', label: 'This Month' },
            { id: 'last_month', label: 'Last Month' },
            { id: 'this_quarter', label: 'Quarter' },
            { id: 'this_year', label: 'Year' },
            { id: 'custom', label: 'Custom' },
          ].map(p => (
            <button
              key={p.id}
              onClick={() => p.id !== 'custom' ? applyPreset(p.id) : setDatePreset('custom')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                datePreset === p.id ? 'bg-slate-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {datePreset === 'custom' && (
          <div className="flex gap-2 mt-2 items-end">
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm" />
            <span className="text-gray-400 text-sm">to</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm" />
          </div>
        )}
        <p className="text-xs text-slate-400 mt-2">{period?.start} to {period?.end}</p>
      </div>

      {/* Top-Level Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-slate-500">Total Revenue</p>
            <p className="text-xl font-bold text-slate-900 mt-1">{formatCurrency(overall.total_revenue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-slate-500">Online Revenue</p>
            <p className="text-xl font-bold text-blue-600 mt-1">{formatCurrency(overall.online_revenue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-slate-500">Events</p>
            <p className="text-xl font-bold text-purple-600 mt-1">{overall.event_count}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-slate-500">TRP Revenue</p>
            <p className="text-xl font-bold text-emerald-600 mt-1">{formatCurrency(overall.booking_revenue)}</p>
            <p className="text-[10px] text-gray-400">Bookings: {overall.total_bookings}</p>
          </CardContent>
        </Card>
      </div>

      {/* Facility Cards */}
      <div>
        <h2 className="font-semibold text-slate-700 mb-3">Facilities</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Object.entries(by_facility).map(([fid, stats]) => (
            <Card key={fid} className="overflow-hidden">
              <CardContent className="pt-5 pb-4">
                <h3 className="font-bold text-slate-900 mb-3">{stats.name}</h3>
                <div className="grid grid-cols-2 gap-y-3 text-sm">
                  <div>
                    <p className="text-xs text-slate-500">Revenue</p>
                    <p className="font-bold">{formatCurrency(stats.total_revenue)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Online Revenue</p>
                    <p className="font-bold text-blue-600">{formatCurrency(stats.online_revenue)}</p>
                  </div>
                  <button
                    onClick={() => openDrilldown('dues', fid, stats.name)}
                    className="text-left hover:bg-orange-50 rounded-lg p-1 -ml-1 transition-colors"
                    data-testid={`dues-${fid}`}
                  >
                    <p className="text-xs text-slate-500">Pending Dues</p>
                    <p className="font-bold text-orange-600">{formatCurrency(stats.pending_amount)}</p>
                  </button>
                  <button
                    onClick={() => openDrilldown('events', fid, stats.name)}
                    className="text-left hover:bg-purple-50 rounded-lg p-1 -ml-1 transition-colors"
                    data-testid={`events-${fid}`}
                  >
                    <p className="text-xs text-slate-500">Events</p>
                    <p className="font-bold text-purple-600">{stats.event_count}</p>
                  </button>
                  <div>
                    <p className="text-xs text-slate-500">Community Games</p>
                    <p className="font-bold">{stats.community_games_count}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Bookings</p>
                    <p className="font-bold">{stats.total_bookings}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============== Reports Tab ==============
const ReportsTab = () => {
  const [loading, setLoading] = useState(false);
  const [reportsData, setReportsData] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilities, setSelectedFacilities] = useState('all');
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadFacilities();
  }, []);

  useEffect(() => {
    loadReports();
  }, [dateRange, selectedFacilities]);

  const loadFacilities = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/superadmin/facilities`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFacilities(data);
      }
    } catch (error) {
      console.error('Error loading facilities:', error);
    }
  };

  const loadReports = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        start_date: dateRange.start,
        end_date: dateRange.end,
        facility_ids: selectedFacilities
      });
      const response = await fetch(`${API_URL}/api/superadmin/reports?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load reports');
      const data = await response.json();
      setReportsData(data);
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
        <p className="text-slate-500">Detailed analytics across facilities</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 cursor-pointer min-w-[150px]"
                data-testid="report-start-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 cursor-pointer min-w-[150px]"
                data-testid="report-end-date"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-slate-700 mb-1">Facility</label>
              <select
                value={selectedFacilities}
                onChange={(e) => setSelectedFacilities(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                data-testid="facility-filter"
              >
                <option value="all">All Facilities</option>
                {facilities.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      ) : reportsData && (
        <>
          {/* Overall Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-slate-500">Total Bookings</p>
                <p className="text-2xl font-bold">{reportsData.overall.total_bookings}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-slate-500">Total Revenue</p>
                <p className="text-2xl font-bold">{formatCurrency(reportsData.overall.total_revenue)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-slate-500">Collected</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(reportsData.overall.total_collected)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-slate-500">Pending</p>
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(reportsData.overall.pending_amount)}</p>
              </CardContent>
            </Card>
          </div>

          {/* By Facility Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Breakdown by Facility</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {Object.entries(reportsData.by_facility).map(([facilityId, data]) => (
                  <div key={facilityId} className="border-b border-slate-200 pb-4 last:border-0">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-slate-900">{data.name}</h3>
                      <span className="text-lg font-bold text-slate-900">{formatCurrency(data.revenue)}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Bookings</p>
                        <p className="font-medium">{data.bookings}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Collected</p>
                        <p className="font-medium text-emerald-600">{formatCurrency(data.collected)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">By Source</p>
                        <div className="space-y-1">
                          {Object.entries(data.by_source || {}).map(([source, amount]) => (
                            <p key={source} className="font-medium capitalize">{source}: {formatCurrency(amount)}</p>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-slate-500">By Sport</p>
                        <div className="space-y-1">
                          {Object.entries(data.by_sport || {}).map(([sport, amount]) => (
                            <p key={sport} className="font-medium capitalize">{sport}: {formatCurrency(amount)}</p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// ============== Facilities Tab ==============
const FacilitiesTab = () => {
  const [loading, setLoading] = useState(true);
  const [facilities, setFacilities] = useState([]);
  const [editingFacility, setEditingFacility] = useState(null);
  const [editingCourt, setEditingCourt] = useState(null);
  const [editingPricing, setEditingPricing] = useState(null);
  const [showNewFacility, setShowNewFacility] = useState(false);
  const [showNewCourt, setShowNewCourt] = useState(null); // facility ID for new court

  useEffect(() => {
    loadFacilities();
  }, []);

  const loadFacilities = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/superadmin/facilities`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load facilities');
      const data = await response.json();
      setFacilities(data);
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFacility = async (facility) => {
    const confirmText = `Delete facility "${facility.name}"?\n\n` +
      `This will deactivate it — the venue will be HIDDEN from the public Explore page and Book flow. ` +
      `Existing bookings and historical data are preserved.\n\n` +
      `Type the facility name to confirm.`;
    const typed = window.prompt(confirmText, '');
    if (typed === null) return;
    if (typed.trim() !== facility.name) {
      toast({ title: 'Cancelled', description: 'Name did not match — facility not deleted.', variant: 'destructive' });
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/facilities/${facility.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Delete failed');
      }
      toast({ title: 'Deleted', description: `${facility.name} has been deactivated.` });
      loadFacilities();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Facilities</h1>
          <p className="text-slate-500">Manage venues, courts, and pricing</p>
        </div>
        <Button onClick={() => setShowNewFacility(true)} data-testid="add-facility-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Facility
        </Button>
      </div>

      {/* Facilities List */}
      <div className="space-y-4">
        {facilities.map((facility) => (
          <Card key={facility.id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-lg">{facility.name}</CardTitle>
                    {facility.status === 'inactive' && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-red-100 text-red-700" data-testid={`facility-inactive-${facility.id}`}>
                        Inactive
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500">{facility.address || facility.location}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setEditingPricing(facility)}
                  >
                    <DollarSign className="w-4 h-4 mr-1" /> Pricing
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setEditingFacility(facility)}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                    onClick={() => handleDeleteFacility(facility)}
                    data-testid={`delete-facility-${facility.id}`}
                    title="Delete facility (deactivate)"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Operating Hours & Sports */}
              <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                <div>
                  <p className="text-slate-500">Operating Hours</p>
                  <p className="font-medium">
                    {facility.operating_hours?.start || '06:00'} - {facility.operating_hours?.end || '23:00'}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Sports</p>
                  <div className="flex flex-wrap gap-1">
                    {(facility.sports || []).map(sport => (
                      <span key={sport} className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs capitalize">
                        {sport}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Courts */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-slate-700">Courts ({(facility.courts || []).length})</p>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowNewCourt(facility.id)}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Court
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {(facility.courts || []).map((court) => (
                    <div 
                      key={court.id} 
                      className="flex items-center justify-between p-2 bg-slate-50 rounded"
                    >
                      <div>
                        <p className="font-medium text-sm">{court.name}</p>
                        <p className="text-xs text-slate-500 capitalize">{court.sport} • {court.court_type}</p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setEditingCourt({ facility, court })}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Facility Modal */}
      {editingFacility && (
        <FacilityModal 
          facility={editingFacility} 
          onClose={() => setEditingFacility(null)}
          onSave={() => {
            setEditingFacility(null);
            loadFacilities();
          }}
        />
      )}

      {/* New Facility Modal */}
      {showNewFacility && (
        <FacilityModal 
          facility={null} 
          onClose={() => setShowNewFacility(false)}
          onSave={() => {
            setShowNewFacility(false);
            loadFacilities();
          }}
        />
      )}

      {/* Edit Court Modal */}
      {editingCourt && (
        <CourtModal 
          facilityId={editingCourt.facility.id}
          court={editingCourt.court} 
          onClose={() => setEditingCourt(null)}
          onSave={() => {
            setEditingCourt(null);
            loadFacilities();
          }}
        />
      )}

      {/* New Court Modal */}
      {showNewCourt && (
        <CourtModal 
          facilityId={showNewCourt}
          court={null} 
          onClose={() => setShowNewCourt(null)}
          onSave={() => {
            setShowNewCourt(null);
            loadFacilities();
          }}
        />
      )}

      {/* Edit Pricing Modal */}
      {editingPricing && (
        <PricingModal 
          facility={editingPricing} 
          onClose={() => setEditingPricing(null)}
          onSave={() => {
            setEditingPricing(null);
            loadFacilities();
          }}
        />
      )}
    </div>
  );
};

// ============== Facility Modal ==============
const FacilityModal = ({ facility, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: facility?.name || '',
    slug: facility?.slug || '',
    description: facility?.description || '',
    address: facility?.address || '',
    location: facility?.location || '',
    phone: facility?.phone || '',
    email: facility?.email || '',
    sports: (facility?.sports || []).join(', '),
    operating_hours_start: facility?.operating_hours?.start || '06:00',
    operating_hours_end: facility?.operating_hours?.end || '23:00',
    slot_duration: facility?.slot_duration || 30
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: formData.name,
        slug: formData.slug,
        description: formData.description,
        address: formData.address,
        location: formData.location,
        phone: formData.phone,
        email: formData.email,
        sports: formData.sports.split(',').map(s => s.trim()).filter(Boolean),
        operating_hours: {
          start: formData.operating_hours_start,
          end: formData.operating_hours_end
        },
        slot_duration: parseInt(formData.slot_duration)
      };

      const url = facility 
        ? `${API_URL}/api/superadmin/facilities/${facility.id}`
        : `${API_URL}/api/superadmin/facilities`;
      
      const response = await fetch(url, {
        method: facility ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save facility');
      }

      toast({ title: 'Success', description: `Facility ${facility ? 'updated' : 'created'} successfully` });
      onSave();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-semibold">{facility ? 'Edit Facility' : 'New Facility'}</h2>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="South United FC"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Slug *</label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="south_united"
                disabled={!!facility}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="HSR Layout, Bangalore"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Sports (comma separated)</label>
            <input
              type="text"
              value={formData.sports}
              onChange={(e) => setFormData(prev => ({ ...prev, sports: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              placeholder="Football, Padel, Pickleball"
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Open Time</label>
              <input
                type="time"
                value={formData.operating_hours_start}
                onChange={(e) => setFormData(prev => ({ ...prev, operating_hours_start: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Close Time</label>
              <input
                type="time"
                value={formData.operating_hours_end}
                onChange={(e) => setFormData(prev => ({ ...prev, operating_hours_end: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Slot (min)</label>
              <select
                value={formData.slot_duration}
                onChange={(e) => setFormData(prev => ({ ...prev, slot_duration: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="30">30</option>
                <option value="60">60</option>
              </select>
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {facility ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============== Court Modal ==============
const CourtModal = ({ facilityId, court, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    id: court?.id || '',
    name: court?.name || '',
    sport: court?.sport || 'football',
    court_type: court?.court_type || '',
    status: court?.status || 'active',
    display_order: court?.display_order || 1
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = court 
        ? `${API_URL}/api/superadmin/facilities/${facilityId}/courts/${court.id}`
        : `${API_URL}/api/superadmin/facilities/${facilityId}/courts`;
      
      const response = await fetch(url, {
        method: court ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save court');
      }

      toast({ title: 'Success', description: `Court ${court ? 'updated' : 'added'} successfully` });
      onSave();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-semibold">{court ? 'Edit Court' : 'Add Court'}</h2>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Court ID *</label>
            <input
              type="text"
              value={formData.id}
              onChange={(e) => setFormData(prev => ({ ...prev, id: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              placeholder="court_5aside_1"
              disabled={!!court}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              placeholder="5-Aside Court 1"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Sport</label>
              <select
                value={formData.sport}
                onChange={(e) => setFormData(prev => ({ ...prev, sport: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="football">Football</option>
                <option value="padel">Padel</option>
                <option value="pickleball">Pickleball</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Court Type</label>
              <input
                type="text"
                value={formData.court_type}
                onChange={(e) => setFormData(prev => ({ ...prev, court_type: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="5aside"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="active">Active</option>
                <option value="maintenance">Maintenance</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Display Order</label>
              <input
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              />
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============== Pricing Modal ==============
const PricingModal = ({ facility, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pricingData, setPricingData] = useState({});

  useEffect(() => {
    loadPricing();
  }, []);

  const loadPricing = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/superadmin/facilities/${facility.id}/pricing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPricingData(data.pricing || {});
      }
    } catch (error) {
      console.error('Error loading pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/superadmin/facilities/${facility.id}/pricing`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ pricing: pricingData })
      });

      if (!response.ok) throw new Error('Failed to save pricing');

      toast({ title: 'Success', description: 'Pricing updated successfully' });
      onSave();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const updatePrice = (courtType, period, value) => {
    setPricingData(prev => ({
      ...prev,
      [courtType]: {
        ...prev[courtType],
        [period]: parseInt(value) || 0
      }
    }));
  };

  // Get court types from facility courts
  const courtTypes = [...new Set((facility.courts || []).map(c => c.court_type))];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-semibold">Pricing: {facility.name}</h2>
          <p className="text-sm text-slate-500">Set prices per 30-minute slot</p>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : (
            <div className="space-y-4">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 text-sm font-medium text-slate-700">Court Type</th>
                    <th className="text-left py-2 text-sm font-medium text-slate-700">Off-Peak (₹)</th>
                    <th className="text-left py-2 text-sm font-medium text-slate-700">Peak (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {courtTypes.map(courtType => (
                    <tr key={courtType} className="border-b">
                      <td className="py-3 capitalize font-medium">{courtType.replace(/_/g, ' ')}</td>
                      <td className="py-3">
                        <input
                          type="number"
                          value={pricingData[courtType]?.off_peak || ''}
                          onChange={(e) => updatePrice(courtType, 'off_peak', e.target.value)}
                          className="w-24 px-3 py-2 border border-slate-300 rounded-lg"
                          placeholder="0"
                        />
                      </td>
                      <td className="py-3">
                        <input
                          type="number"
                          value={pricingData[courtType]?.peak || ''}
                          onChange={(e) => updatePrice(courtType, 'peak', e.target.value)}
                          className="w-24 px-3 py-2 border border-slate-300 rounded-lg"
                          placeholder="0"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {courtTypes.length === 0 && (
                <p className="text-slate-500 text-center py-4">Add courts first to configure pricing</p>
              )}
            </div>
          )}
        </div>
        <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Pricing
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============== Admin Users Tab ==============
const AdminUsersTab = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [showNewUser, setShowNewUser] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [usersRes, facilitiesRes] = await Promise.all([
        fetch(`${API_URL}/api/superadmin/users`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/superadmin/facilities`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (usersRes.ok) setUsers(await usersRes.json());
      if (facilitiesRes.ok) setFacilities(await facilitiesRes.json());
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/superadmin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to deactivate user');

      toast({ title: 'Success', description: 'User deactivated' });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Admin Users</h1>
          <p className="text-slate-500">Manage admin accounts across facilities</p>
        </div>
        <Button onClick={() => setShowNewUser(true)} data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" /> Add User
        </Button>
      </div>

      {/* Users Table */}
      <Card>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">Name</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">Role</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">Facility</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">Status</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} className="border-b hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm text-slate-500">{user.phone}</p>
                    </td>
                    <td className="py-3 px-4 text-sm">{user.email}</td>
                    <td className="py-3 px-4">
                      <span className={`
                        px-2 py-1 rounded text-xs
                        ${user.role === 'admin' ? 'bg-blue-100 text-blue-700' : 
                          user.role === 'venue_manager' ? 'bg-amber-100 text-amber-700' :
                          user.role === 'venue_owner' ? 'bg-emerald-100 text-emerald-700' :
                          user.role === 'venue_finance' ? 'bg-violet-100 text-violet-700' :
                          'bg-slate-100 text-slate-700'}
                      `}>
                        {user.role === 'venue_manager' ? 'Venue Manager' :
                          user.role === 'venue_owner' ? 'Venue Owner' :
                          user.role === 'venue_finance' ? 'Venue Finance' : user.role}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">{user.facility_name || 'N/A'}</td>
                    <td className="py-3 px-4">
                      <span className={`
                        px-2 py-1 rounded text-xs
                        ${user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}
                      `}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setEditingUser(user)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {user.is_active && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDelete(user.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Edit User Modal */}
      {editingUser && (
        <UserModal 
          user={editingUser} 
          facilities={facilities}
          onClose={() => setEditingUser(null)}
          onSave={() => {
            setEditingUser(null);
            loadData();
          }}
        />
      )}

      {/* New User Modal */}
      {showNewUser && (
        <UserModal 
          user={null} 
          facilities={facilities}
          onClose={() => setShowNewUser(false)}
          onSave={() => {
            setShowNewUser(false);
            loadData();
          }}
        />
      )}
    </div>
  );
};

// ============== User Modal ==============
const UserModal = ({ user, facilities, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    password: '',
    role: user?.role || 'admin',
    facility_id: user?.facility_id || '',
    is_active: user?.is_active ?? true
  });

  const handleSave = async () => {
    if (!formData.name || !formData.email || !formData.facility_id) {
      toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }

    if (!user && !formData.password) {
      toast({ title: 'Error', description: 'Password is required for new users', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { ...formData };
      if (!payload.password) delete payload.password;

      const url = user 
        ? `${API_URL}/api/superadmin/users/${user.id}`
        : `${API_URL}/api/superadmin/users`;
      
      const response = await fetch(url, {
        method: user ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save user');
      }

      toast({ title: 'Success', description: `User ${user ? 'updated' : 'created'} successfully` });
      onSave();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-semibold">{user ? 'Edit User' : 'New Admin User'}</h2>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
            <input
              type="text"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Password {user ? '(leave blank to keep current)' : '*'}
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                data-testid="user-role-select"
              >
                <option value="admin">Admin (Full Access)</option>
                <option value="venue_manager">Venue Manager (Read-Only)</option>
                <option value="venue_owner">Venue Owner (Read-Only: Calendar + Reports)</option>
                <option value="venue_finance">Venue Finance (Read-Only: Calendar + Bookings + Finance)</option>
                <option value="manager">Manager</option>
                <option value="staff">Staff</option>
              </select>
              {formData.role === 'venue_manager' && (
                <p className="text-xs text-amber-600 mt-1">
                  Venue managers can view bookings but cannot edit, cancel, or modify settings.
                </p>
              )}
              {formData.role === 'venue_owner' && (
                <p className="text-xs text-amber-600 mt-1">
                  Read-only, scoped to this one venue. Sees only the Calendar and Reports. Sets their own password on first login.
                </p>
              )}
              {formData.role === 'venue_finance' && (
                <p className="text-xs text-amber-600 mt-1">
                  Read-only, scoped to this one venue. Sees Calendar, Bookings and Finance (view/filter/download). Sets their own password on first login.
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Facility *</label>
              <select
                value={formData.facility_id}
                onChange={(e) => setFormData(prev => ({ ...prev, facility_id: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="">Select facility</option>
                {facilities.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          </div>
          {user && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                className="w-4 h-4"
              />
              <label htmlFor="is_active" className="text-sm text-slate-700">Active</label>
            </div>
          )}
        </div>
        <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {user ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============== Coupons Tab ==============
const CouponsTab = () => {
  const [loading, setLoading] = useState(true);
  const [coupons, setCoupons] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [showNewCoupon, setShowNewCoupon] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  useEffect(() => {
    loadData();
  }, [includeInactive]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [couponsRes, facilitiesRes] = await Promise.all([
        fetch(`${API_URL}/api/coupons?include_inactive=${includeInactive}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/superadmin/facilities`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (couponsRes.ok) setCoupons(await couponsRes.json());
      if (facilitiesRes.ok) setFacilities(await facilitiesRes.json());
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load coupons', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (coupon) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = coupon.is_active 
        ? `${API_URL}/api/coupons/${coupon.id}`
        : `${API_URL}/api/coupons/${coupon.id}/activate`;
      
      const response = await fetch(endpoint, {
        method: coupon.is_active ? 'DELETE' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to update coupon');

      toast({ 
        title: 'Success', 
        description: `Coupon ${coupon.is_active ? 'deactivated' : 'activated'} successfully` 
      });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const isExpired = (endDate) => {
    return new Date(endDate) < new Date();
  };

  const isNotStarted = (startDate) => {
    return new Date(startDate) > new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Coupons & Discounts</h1>
          <p className="text-slate-500">Manage discount codes across venues</p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="w-4 h-4 rounded"
            />
            Show inactive
          </label>
          <Button onClick={() => setShowNewCoupon(true)} data-testid="add-coupon-btn">
            <Plus className="w-4 h-4 mr-2" /> Create Coupon
          </Button>
        </div>
      </div>

      {/* Coupons List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {coupons.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="pt-6 text-center text-slate-500">
              No coupons found. Create your first coupon to offer discounts!
            </CardContent>
          </Card>
        ) : (
          coupons.map((coupon) => (
            <Card key={coupon.id} className={`${!coupon.is_active ? 'opacity-60' : ''}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Tag className="w-5 h-5 text-emerald-600" />
                    <CardTitle className="text-lg font-mono">{coupon.code}</CardTitle>
                  </div>
                  <div className="flex items-center gap-1">
                    {!coupon.is_active && (
                      <span className="px-2 py-1 bg-slate-200 text-slate-600 rounded text-xs">Inactive</span>
                    )}
                    {coupon.is_active && isExpired(coupon.end_date) && (
                      <span className="px-2 py-1 bg-red-100 text-red-600 rounded text-xs">Expired</span>
                    )}
                    {coupon.is_active && isNotStarted(coupon.start_date) && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-600 rounded text-xs">Upcoming</span>
                    )}
                    {coupon.is_active && !isExpired(coupon.end_date) && !isNotStarted(coupon.start_date) && (
                      <span className="px-2 py-1 bg-emerald-100 text-emerald-600 rounded text-xs">Active</span>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-3xl font-bold text-emerald-600">{coupon.discount_percent}% OFF</div>
                
                {coupon.description && (
                  <p className="text-sm text-slate-600">{coupon.description}</p>
                )}
                
                <div className="text-xs text-slate-500 space-y-1">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3 h-3" />
                    <span>{coupon.start_date} to {coupon.end_date}</span>
                  </div>
                  <div>
                    <strong>Venues:</strong> {coupon.facility_names?.join(', ') || 'All Facilities'}
                  </div>
                  {coupon.min_booking_amount && (
                    <div><strong>Min Amount:</strong> ₹{coupon.min_booking_amount}</div>
                  )}
                  {coupon.max_uses && (
                    <div><strong>Usage:</strong> {coupon.times_used} / {coupon.max_uses}</div>
                  )}
                  {!coupon.max_uses && (
                    <div><strong>Used:</strong> {coupon.times_used} times</div>
                  )}
                </div>

                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => setEditingCoupon(coupon)}
                  >
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button 
                    variant={coupon.is_active ? "destructive" : "default"}
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleToggleActive(coupon)}
                  >
                    {coupon.is_active ? (
                      <>
                        <ToggleRight className="w-3 h-3 mr-1" /> Deactivate
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="w-3 h-3 mr-1" /> Activate
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* New Coupon Modal */}
      {showNewCoupon && (
        <CouponModal 
          coupon={null}
          facilities={facilities}
          onClose={() => setShowNewCoupon(false)}
          onSave={() => {
            setShowNewCoupon(false);
            loadData();
          }}
        />
      )}

      {/* Edit Coupon Modal */}
      {editingCoupon && (
        <CouponModal 
          coupon={editingCoupon}
          facilities={facilities}
          onClose={() => setEditingCoupon(null)}
          onSave={() => {
            setEditingCoupon(null);
            loadData();
          }}
        />
      )}
    </div>
  );
};

// ============== Coupon Modal ==============
const CouponModal = ({ coupon, facilities, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  const nextMonth = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  
  const [formData, setFormData] = useState({
    code: coupon?.code || '',
    description: coupon?.description || '',
    discount_percent: coupon?.discount_percent || 10,
    facility_ids: coupon?.facility_ids || [],
    start_date: coupon?.start_date || today,
    end_date: coupon?.end_date || nextMonth,
    max_uses: coupon?.max_uses || '',
    min_booking_amount: coupon?.min_booking_amount || '',
    show_on_booking: coupon?.show_on_booking || false
  });

  const handleSave = async () => {
    if (!formData.code.trim()) {
      toast({ title: 'Error', description: 'Coupon code is required', variant: 'destructive' });
      return;
    }
    if (formData.discount_percent <= 0 || formData.discount_percent > 100) {
      toast({ title: 'Error', description: 'Discount must be between 1 and 100%', variant: 'destructive' });
      return;
    }
    if (formData.end_date < formData.start_date) {
      toast({ title: 'Error', description: 'End date must be after start date', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        code: formData.code.toUpperCase(),
        description: formData.description || null,
        discount_percent: parseFloat(formData.discount_percent),
        facility_ids: formData.facility_ids,
        start_date: formData.start_date,
        end_date: formData.end_date,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        min_booking_amount: formData.min_booking_amount ? parseFloat(formData.min_booking_amount) : null,
        show_on_booking: !!formData.show_on_booking
      };

      const url = coupon 
        ? `${API_URL}/api/coupons/${coupon.id}`
        : `${API_URL}/api/coupons`;
      
      const response = await fetch(url, {
        method: coupon ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save coupon');
      }

      toast({ title: 'Success', description: `Coupon ${coupon ? 'updated' : 'created'} successfully` });
      onSave();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const toggleFacility = (facilityId) => {
    setFormData(prev => ({
      ...prev,
      facility_ids: prev.facility_ids.includes(facilityId)
        ? prev.facility_ids.filter(id => id !== facilityId)
        : [...prev.facility_ids, facilityId]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-semibold">{coupon ? 'Edit Coupon' : 'Create New Coupon'}</h2>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Coupon Code *</label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono uppercase"
              placeholder="SUMMER25"
              disabled={!!coupon}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              placeholder="Summer sale discount"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Discount Percentage *</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={formData.discount_percent}
                onChange={(e) => setFormData(prev => ({ ...prev, discount_percent: e.target.value }))}
                className="w-24 px-3 py-2 border border-slate-300 rounded-lg"
                min="1"
                max="100"
              />
              <span className="text-lg font-semibold text-slate-600">%</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Start Date *</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">End Date *</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Applicable Venues
              <span className="font-normal text-slate-500 ml-1">(Leave empty for all venues)</span>
            </label>
            <div className="border border-slate-300 rounded-lg p-3 max-h-36 overflow-y-auto space-y-2">
              {facilities.map(facility => (
                <label key={facility.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.facility_ids.includes(facility.id)}
                    onChange={() => toggleFacility(facility.id)}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm">{facility.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Max Uses</label>
              <input
                type="number"
                value={formData.max_uses}
                onChange={(e) => setFormData(prev => ({ ...prev, max_uses: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="Unlimited"
                min="1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Min Booking (₹)</label>
              <input
                type="number"
                value={formData.min_booking_amount}
                onChange={(e) => setFormData(prev => ({ ...prev, min_booking_amount: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="No minimum"
                min="0"
              />
            </div>
          </div>

          <div className="flex items-start justify-between gap-3 pt-2 border-t border-slate-100">
            <div>
              <label className="block text-sm font-medium text-slate-700">Show on booking page</label>
              <p className="text-xs text-slate-500 mt-0.5">On = public promo shown to customers on the grid. Off = private code (works only if pasted).</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={!!formData.show_on_booking}
              data-testid="coupon-show-on-booking-toggle"
              onClick={() => setFormData(prev => ({ ...prev, show_on_booking: !prev.show_on_booking }))}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${formData.show_on_booking ? 'bg-emerald-500' : 'bg-slate-300'}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${formData.show_on_booking ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>
        <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {coupon ? 'Update' : 'Create'} Coupon
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============== Main Super Admin Dashboard ==============
// ============== Aggregator Commission Tab ==============
const AGG_PARTNERS = [
  { key: 'playo', label: 'Playo' },
  { key: 'hudle', label: 'Hudle' },
  { key: 'district', label: 'District' },
];

const CommissionTab = () => {
  const [loading, setLoading] = useState(true);
  const [venues, setVenues] = useState([]);
  const [selected, setSelected] = useState('');
  const [values, setValues] = useState({ playo: 0, hudle: 0, district: 0 });
  const [saving, setSaving] = useState(false);

  const loadVenues = async () => {
    setLoading(true);
    try {
      const data = await api.request('/finance/venues');
      const list = data.venues || [];
      setVenues(list);
      if (list.length && !selected) {
        setSelected(list[0].id);
        const c = list[0].aggregator_commission || {};
        setValues({ playo: c.playo ?? 0, hudle: c.hudle ?? 0, district: c.district ?? 0 });
      }
    } catch (e) {
      toast({ title: 'Failed to load venues', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadVenues(); /* eslint-disable-next-line */ }, []);

  const onSelectVenue = (id) => {
    setSelected(id);
    const v = venues.find(x => x.id === id);
    const c = (v && v.aggregator_commission) || {};
    setValues({ playo: c.playo ?? 0, hudle: c.hudle ?? 0, district: c.district ?? 0 });
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const data = await api.request('/finance/venue-commission', {
        method: 'PUT',
        body: JSON.stringify({
          facility_id: selected,
          playo: Number(values.playo), hudle: Number(values.hudle), district: Number(values.district),
        }),
      });
      // reflect saved values back into the venues list
      setVenues(prev => prev.map(v => v.id === selected ? { ...v, aggregator_commission: data.aggregator_commission } : v));
      toast({ title: 'Commission saved', description: 'Aggregator commission updated for this venue.' });
    } catch (e) {
      toast({ title: 'Save failed', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const selectedVenue = venues.find(v => v.id === selected);

  return (
    <div className="p-4 md:p-6 max-w-3xl" data-testid="commission-tab">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-slate-900">Aggregator Commission</h1>
        <p className="text-sm text-slate-500 mt-1">Select a venue and set each partner's commission %. Finance shows aggregator bookings as gross and computes expected-net using these rates.</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading venues…</div>
      ) : (
        <Card>
          <CardContent className="p-5 space-y-5">
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Venue</label>
              <select
                value={selected}
                onChange={(e) => onSelectVenue(e.target.value)}
                className="w-full h-10 border border-slate-300 rounded-md px-3 text-sm"
                data-testid="commission-venue-select"
              >
                {venues.map(v => (<option key={v.id} value={v.id}>{v.name || v.id}</option>))}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {AGG_PARTNERS.map(p => (
                <div key={p.key}>
                  <label className="text-xs font-medium text-slate-500 block mb-1">{p.label} commission %</label>
                  <div className="relative">
                    <input
                      type="number" min="0" max="100" step="0.1"
                      value={values[p.key]}
                      onChange={(e) => setValues(v => ({ ...v, [p.key]: e.target.value }))}
                      className="w-full h-10 border border-slate-300 rounded-md px-3 pr-8 text-sm"
                      data-testid={`commission-input-${p.key}`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                  </div>
                </div>
              ))}
            </div>

            {selectedVenue && (
              <p className="text-xs text-slate-400">
                Current saved: Playo {selectedVenue.aggregator_commission?.playo ?? 0}% · Hudle {selectedVenue.aggregator_commission?.hudle ?? 0}% · District {selectedVenue.aggregator_commission?.district ?? 0}%
              </p>
            )}

            <div className="flex justify-end">
              <Button onClick={save} disabled={saving || !selected} data-testid="commission-save-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Save commission
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============== SEC-005 Access Audit Tab ==============
const AccessAuditTab = () => {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/superadmin/readonly-access`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setAccounts(data.accounts || []);
        }
      } catch (e) {
        toast({ title: 'Error', description: 'Failed to load access audit', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch { return iso; }
  };

  const roleBadge = (role) => role === 'venue_owner'
    ? <span className="px-2 py-1 rounded text-xs bg-emerald-100 text-emerald-700">Venue Owner</span>
    : <span className="px-2 py-1 rounded text-xs bg-violet-100 text-violet-700">Venue Finance</span>;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const active = accounts.filter(a => a.is_active);

  return (
    <div className="space-y-6" data-testid="access-audit-tab">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-emerald-600" /> Access Audit
        </h1>
        <p className="text-slate-500">Read-only venue accounts (owner / finance) — assigned venue, status and last login</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card><CardContent className="pt-6"><p className="text-sm text-slate-500">Total read-only accounts</p><p className="text-2xl font-bold" data-testid="audit-total">{accounts.length}</p></CardContent></Card>
        <Card><CardContent className="pt-6"><p className="text-sm text-slate-500">Active</p><p className="text-2xl font-bold text-green-600">{active.length}</p></CardContent></Card>
        <Card><CardContent className="pt-6"><p className="text-sm text-slate-500">Venue Owners</p><p className="text-2xl font-bold">{accounts.filter(a => a.role === 'venue_owner').length}</p></CardContent></Card>
        <Card><CardContent className="pt-6"><p className="text-sm text-slate-500">Venue Finance</p><p className="text-2xl font-bold">{accounts.filter(a => a.role === 'venue_finance').length}</p></CardContent></Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          {accounts.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No read-only venue accounts yet. Create one from Admin Users.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">Role</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">Assigned Venue</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">Last Login</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">Last IP</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map(a => (
                    <tr key={a.id} className="border-b hover:bg-slate-50" data-testid={`audit-row-${a.id}`}>
                      <td className="py-3 px-4">
                        <p className="font-medium">{a.name}</p>
                        <p className="text-xs text-slate-500">{a.phone}</p>
                      </td>
                      <td className="py-3 px-4 text-sm">{a.email}</td>
                      <td className="py-3 px-4">{roleBadge(a.role)}</td>
                      <td className="py-3 px-4 text-sm font-medium text-slate-700">{a.facility_name}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs ${a.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {a.is_active ? 'Active' : 'Revoked'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600" data-testid={`audit-lastlogin-${a.id}`}>{fmt(a.last_login_at)}</td>
                      <td className="py-3 px-4 text-xs text-slate-500">{a.last_login_ip || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};



const SuperAdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'superadmin')) {
      navigate('/admin/login');
    }
  }, [user, authLoading, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!user || user.role !== 'superadmin') {
    return null;
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardOverview />;
      case 'reports':
        return <ReportsTab />;
      case 'facilities':
        return <FacilitiesTab />;
      case 'coupons':
        return <CouponsTab />;
      case 'commission':
        return <CommissionTab />;
      case 'users':
        return <AdminUsersTab />;
      case 'access_audit':
        return <AccessAuditTab />;
      default:
        return <DashboardOverview />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <SuperAdminSidebar 
        activeTab={activeTab} 
        setActiveTab={setActiveTab}
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
      />
      
      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-slate-600"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500">Welcome, {user?.name}</span>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
